import { ApiStatus, UCenterServices } from 'network'
import { toCamelCase } from 'utils'
import { useLanguageStore } from '..'
import { SvIds } from '../constants/static-table-sv-id'
import { useChangeLanguage } from '../hooks/use-change-language'
import { storageDB, StorageIndexDBEnum } from '../storage/StorageDB'
import { useExchangeInfoStore } from './use-exchange-info'

export enum GeeTestType {
  Register = 1,
  Unregister = 2,
}

export const useStaticTableDataStore = defineStore('staticTableData', () => {
  const staticTableDataMap = ref<ApiResponse.StaticTableData>({} as ApiResponse.StaticTableData)
  /** 静态表版本信息 */
  const staticTableVersionInfo = ref<ApiResponse.StaticTableVersionInfo>(JSON.parse(window.localStorage.getItem(StorageIndexDBEnum.StaticTableVersionInfo) || '{}'))

  /** 是否缓存过静态表的版本信息 */
  const isCacheStaticVersion = Object.keys(staticTableVersionInfo.value).length > 0

  const loadingStaticTableData = ref(true)

  const exchangeInfoStore = useExchangeInfoStore()
  const languageStore = useLanguageStore()

  /** 缓存静态表数据 */
  const cacheStaticTableDataBySvId = async (data: ApiResponse.StaticTableData) => {
    let cacheData = await storageDB.get<ApiResponse.StaticTableData>(StorageIndexDBEnum.StaticTableData)
    if (cacheData) {
      Object.keys(data).forEach((svId) => {
        const id = svId as unknown as SvIds
        cacheData && (cacheData[id] = data[id])
      })
    } else {
      cacheData = data
    }
    await storageDB.set(StorageIndexDBEnum.StaticTableData, cacheData)
  }

  const setContractSymbolInfo = async (data: ApiResponse.StaticTableData) => {
    if (data[SvIds.ContractStaticSymbol] && Object.keys(data[SvIds.ContractStaticSymbol]).length > 0) {
      if (!exchangeInfoStore.exchangeInfo) {
        await exchangeInfoStore.getExchangeInfo()
      }
      Object.values(data[SvIds.ContractStaticSymbol]).forEach((item) => {
        const symbolInfo = exchangeInfoStore.getContractSymbolInfoByBaseItemIdAndMarginItemId(item.changeItemId, item.baseItemId)
        if (symbolInfo) {
          item.symbolInfo = symbolInfo
        }
      })
    }
  }

  /**
   * 获取缓存的静态表数据
   * @returns
   */
  const getCacheStaticTableData = async (): Promise<ApiResponse.StaticTableData | null> => {
    const res = await storageDB.get<ApiResponse.StaticTableData>(StorageIndexDBEnum.StaticTableData)
    if (res) {
      await setContractSymbolInfo(res)
    }
    return res
  }

  /**
   * 获取缓存的静态表数据svId
   * @returns
   */
  const getCacheStaticTableDataSvIds = async (): Promise<SvIds[]> => {
    const cacheRes = await getCacheStaticTableData()
    return cacheRes ? (Object.keys(cacheRes).map(Number) as unknown as SvIds[]) : []
  }

  /**
   * 获取静态表数据
   * @param svIds
   */
  const fetchStaticTableData = async (svIds: SvIds[], isForce: boolean = false) => {
    const cacheRes = await getCacheStaticTableData()
    let requestSvIds = svIds
    if (cacheRes) {
      if (!isForce) {
        const ids = await getCacheStaticTableDataSvIds()
        requestSvIds = svIds.filter((svId) => !ids.includes(svId))

        for (const svId of requestSvIds) {
          staticTableDataMap.value[svId] = cacheRes[svId]
        }
      }
    }

    if (!requestSvIds.length) {
      return staticTableDataMap.value
    }

    console.log('requestSvIds', requestSvIds)

    if (requestSvIds.length) {
      const res = await UCenterServices.getStaticTableInfoList({
        svIds: requestSvIds.join(','),
      })
      staticTableDataMap.value = {
        ...staticTableDataMap.value,
        ...res.data,
      }
      await setContractSymbolInfo(staticTableDataMap.value)
      await cacheStaticTableDataBySvId(res.data)
    }
    return staticTableDataMap.value
  }

  /** 获取静态表版本信息 */
  const fetchStaticTableVersionInfo = async () => {
    const res = await UCenterServices.getStaticTableVersionInfo()
    if (res.status !== ApiStatus.Success) {
      return
    }
    const data = res.data
    const cacheSvIds = await getCacheStaticTableDataSvIds()
    if (!isCacheStaticVersion && cacheSvIds.length > 0) {
      // 如果没有缓存过版本信息，但是又存缓存中拿到了缓存过的静态数据的sv-ids, 需要强制刷新一下数据，否则可能丢失数据
      fetchStaticTableData(cacheSvIds)
      window.localStorage.setItem(StorageIndexDBEnum.StaticTableVersionInfo, JSON.stringify(data))
      return
    }

    if (isCacheStaticVersion && cacheSvIds.length > 0) {
      // 如果缓存了版本信息，又有缓存的静态数据ID（svId），需要比对前后的版本信息，如果不一致的，将svId添加到needRefreshSvIds中, 进行刷新
      const needRefreshSvIds: SvIds[] = []
      cacheSvIds.forEach((svId) => {
        const cacheVersionData = Object.values(staticTableVersionInfo.value).find((item) => item.svId === svId)!
        const latestVersionData = Object.values(data).find((item) => item.svId === svId)!
        if (cacheVersionData?.version !== latestVersionData?.version) {
          needRefreshSvIds.push(svId)
        }
      })
      if (needRefreshSvIds.length > 0) {
        await fetchStaticTableData(needRefreshSvIds, true)
      }
    }
    window.localStorage.setItem(StorageIndexDBEnum.StaticTableVersionInfo, JSON.stringify(data))
  }

  const fetchAll = async () => {
    const cacheRes = await getCacheStaticTableData()
    if (cacheRes) {
      staticTableDataMap.value = cacheRes
      loadingStaticTableData.value = false
    }
    await fetchStaticTableVersionInfo()
    const cacheSvIds = await getCacheStaticTableDataSvIds()
    if (!cacheSvIds.length) {
      // 如果没有缓存数据，需要初始化一份通用的缓存数据
      const svIds: SvIds[] = [
        SvIds.ContractStaticSymbol,
        SvIds.StaticItemV2,
        SvIds.StaticSymbol,
        SvIds.MainGeetestCaptchaConfig,
        SvIds.StaticLangI18n,
        SvIds.StaticCurrency,
        SvIds.ContractStaticSymbol,
        SvIds.FuturesStaticSymbol,
        SvIds.StaticCoin,
      ]
      await fetchStaticTableData(svIds)
    }
    loadingStaticTableData.value = false
  }

  const getStaticLangList = () => {
    const langList = staticTableDataMap.value[SvIds.StaticLangI18n]
    return Object.values(langList)
  }

  const langList = computed(() => {
    if (!staticTableDataMap.value[SvIds.StaticLangI18n]) return []
    return Object.values(staticTableDataMap.value[SvIds.StaticLangI18n])
  })

  const currencyList = computed(() => {
    if (!staticTableDataMap.value[SvIds.StaticCurrency]) return []
    return Object.values(staticTableDataMap.value[SvIds.StaticCurrency])
  })

  const getCurrencyNameById = (currencyId: number, defaultName: string = 'USDT') => {
    if (!staticTableDataMap.value[SvIds.StaticCurrency]) return null
    return staticTableDataMap.value[SvIds.StaticCurrency][currencyId]?.abbreviate.toUpperCase() || defaultName
  }

  /** 获取币种信息 */
  const getItemInfoByItemId = (itemId: number) => {
    if (!staticTableDataMap.value[SvIds.StaticItemV2]) return null
    return staticTableDataMap.value[SvIds.StaticItemV2][itemId]
  }

  const getCoinNameByItemId = (itemId: ItemId) => {
    if (!staticTableDataMap.value[SvIds.StaticItemV2]) return ''
    return staticTableDataMap.value[SvIds.StaticItemV2][itemId]?.itemName.toUpperCase()
  }

  const staticCoinChains = computed(() => {
    if (!staticTableDataMap.value[SvIds.StaticCoinChain]) return []
    return Object.values(staticTableDataMap.value[SvIds.StaticCoinChain])
  })

  const fetchStaticTableCoinsChains = async () => {
    fetchStaticTableData([SvIds.StaticCoinChain, SvIds.StaticChain, SvIds.StaticItemV2])
  }

  const staticChains = computed(() => {
    if (!staticTableDataMap.value[SvIds.StaticChain]) return []
    return Object.values(staticTableDataMap.value[SvIds.StaticChain])
  })

  const staticItemV2 = computed(() => {
    if (!staticTableDataMap.value[SvIds.StaticItemV2]) return []
    return Object.values(staticTableDataMap.value[SvIds.StaticItemV2])
  })

  const getFuturesStaticSymbol = (symbolId: SymbolId) => {
    if (!staticTableDataMap.value[SvIds.FuturesStaticSymbol]) return null
    return staticTableDataMap.value[SvIds.FuturesStaticSymbol][symbolId]
  }

  const getContractStaticSymbol = (symbolId: SymbolId) => {
    if (!staticTableDataMap.value[SvIds.ContractStaticSymbol]) return null
    return staticTableDataMap.value[SvIds.ContractStaticSymbol][symbolId]
  }

  /** 获取股票静态表数据 */
  const getStockStaticSymbol = (symbolId: SymbolId) => {
    if (!staticTableDataMap.value[SvIds.StockStaticSymbol]) return null
    return staticTableDataMap.value[SvIds.StockStaticSymbol][symbolId]
  }

  /**
   * 将从`staticCoinChain`和`staticChain`取出数据转换为`StaticTableCoinsChains
   */
  const staticTableCoinsChains = computed(() => {
    return staticCoinChains.value.map((coinChain) => {
      const _chain = staticChains.value.find((item) => item.chainTag === coinChain.chain)
      const item = staticItemV2.value.find((item) => Number(item.itemTag) === coinChain.coinId)
      return {
        itemId: item?.itemId,
        feeRate: coinChain.feeRate,
        feeType: coinChain.feeType,
        addressUrl: _chain?.addressUrl,
        txidUrl: _chain?.txidUrl,
        coinChainStatus: coinChain.status,
        chainStatus: _chain?.status,
        chainTag: _chain?.chainTag,
        rateOut: coinChain.fee,
        confirmNum: _chain?.confirmNum,
        regx: _chain?.regx,
        minin: coinChain.minin,
        minout: coinChain.minout,
        maxout: coinChain.maxout,
        orderBy: coinChain.orderBy,
        description: _chain?.description,
        isDefault: coinChain.isDefault,
        isAvailable: coinChain.status === 1 && _chain?.status === 1,
      }
    })
  })

  const getChain = (data: { itemId?: ItemId; chainTag?: string }) => {
    return staticTableCoinsChains.value.find((item) => item.itemId === data.itemId && item.chainTag?.toUpperCase() === data.chainTag?.toUpperCase())
  }

  const getCurrencyInfoById = (currencyId: number) => {
    if (!staticTableDataMap.value[SvIds.StaticCurrency]) return null
    return staticTableDataMap.value[SvIds.StaticCurrency][currencyId]
  }

  const sotcCurrencyPaymentsDataMap = computed(() => {
    const _sotcCurrencyPaymentsMap = staticTableDataMap.value[SvIds.SotcStaticCurrencyPayment]
    const _currencyMap = staticTableDataMap.value[SvIds.StaticCurrency]
    const _sotcPaymentsMap = staticTableDataMap.value[SvIds.SotcStaticPayment]
    const map: Record<string, ApiResponse.SotcCurrencyPaymentsEntity> = {}

    Object.values(_sotcCurrencyPaymentsMap).forEach((item) => {
      if (item.isUse === 1) {
        const currency = _currencyMap[item.currencyId]
        const payment = _sotcPaymentsMap[item.paymentId]

        if (currency && payment) {
          if (!map[item.currencyId]) {
            map[item.currencyId] = {
              currency,
              payment: [],
            }
          }
          map[item.currencyId].payment.push(payment)
        }
      }
    })
    const currencyIds = Object.keys(map)
    const allCurrencyIds = Object.keys(staticTableDataMap.value[SvIds.StaticCurrency] || {})
    const diff = allCurrencyIds.filter((item) => !currencyIds.includes(item))

    diff.forEach((currencyId: string) => {
      const currency = staticTableDataMap.value[SvIds.StaticCurrency][Number(currencyId)]
      if (!map[currencyId]) {
        map[currencyId] = {
          currency,
          payment: [],
        }
      } else {
        map[currencyId].currency = currency
      }
    })
    return map
  })

  const getAssetsItemByItemId = (itemId: ItemId) => {
    if (!staticTableDataMap.value[SvIds.StaticItemV2]) return null
    return staticTableDataMap.value[SvIds.StaticItemV2][itemId]
  }

  const getCoinByItemId = (itemId: ItemId) => {
    const coinId = getAssetsItemByItemId(itemId)?.itemTag || ''
    if (!coinId) return null
    const coinData = staticTableDataMap.value[SvIds.StaticCoin]
    if (!coinData) return null
    const coin = coinData[Number(coinId)]
    return coin
  }

  const getCoinNameByCoinId = (coinId: number) => {
    const item = getCoinByItemId(coinId)
    const key = toCamelCase(`explain_${languageStore.currentLanguage}`)
    return item?.language[key] || item?.explain || ''
  }

  useChangeLanguage(fetchAll)

  return {
    staticTableDataMap,
    fetchStaticTableData,
    getStaticLangList,
    langList,
    currencyList,
    loadingStaticTableData,
    getItemInfoByItemId,
    getCoinNameByItemId,
    staticCoinChains,
    staticChains,
    fetchStaticTableCoinsChains,
    staticTableCoinsChains,
    getFuturesStaticSymbol,
    getStockStaticSymbol,
    getContractStaticSymbol,
    getCurrencyInfoById,
    sotcCurrencyPaymentsDataMap,
    getChain,
    getCoinByItemId,
    getCoinNameByCoinId,
    getCurrencyNameById,
  }
})
